// ./src/utils/api/DashboardService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";

// GraphQL queries
const DASHBOARD_GET_QUERY = gql`
  query Dashboard($params: DashboardInput) {
    dashboard(params: $params) {
      code
      data {
        planUsage {
          currentPeriod
          planName
          submissionsUsed
          submissionsLimit
          submissionsPercent
          filesUsed
          storageUsedMb
          storageLimitMb
          storagePercent
          formsUsed
          formsLimit
          formsPercent
          daysRemaining
          daysElapsed
          daysInPeriod
          projectedSubmissions
          willExceedLimit
          maxFileSizeMb
          archiveDays
        }
        stats {
          totalSubmissions
          submissionsChange
          successfulSubmissions
          failedSubmissions
          successRate
          spamBlocked
          activeForms
          totalForms
        }
        recentActivity {
          id
          formId
          formName
          formPath
          submittedAt
          status
          submitterEmail
          summary
          remoteIpMasked
        }
        topForms {
          id
          name
          path
          submissionsThisPeriod
          successRate
          lastSubmissionAt
          status
          submissionsQtyLimit
          submissionsCount
        }
        alerts {
          type
          severity
          message
          formId
          formName
          metadata
        }
        onboarding {
          hasCreatedForm
          hasReceivedSubmission
          hasConfiguredEmail
          hasConfiguredIntegration
          hasConfiguredFields
          completionPercent
          showOnboarding
        }
        generatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// TypeScript interfaces
export interface DashboardPlanUsage {
  currentPeriod: number;
  planName: string;
  submissionsUsed: number;
  submissionsLimit: number;
  submissionsPercent: number;
  filesUsed: number;
  storageUsedMb: number;
  storageLimitMb: number;
  storagePercent: number;
  formsUsed: number;
  formsLimit: number;
  formsPercent: number;
  daysRemaining: number;
  daysElapsed: number;
  daysInPeriod: number;
  projectedSubmissions: number | null;
  willExceedLimit: boolean;
  maxFileSizeMb: number;
  archiveDays: number;
}

export interface DashboardStats {
  totalSubmissions: number;
  submissionsChange: number | null;
  successfulSubmissions: number;
  failedSubmissions: number;
  successRate: number;
  spamBlocked: number;
  activeForms: number;
  totalForms: number;
}

export interface DashboardActivityItem {
  id: string;
  formId: string;
  formName: string;
  formPath: string;
  submittedAt: string;
  status: number;
  submitterEmail: string | null;
  summary: string | null;
  remoteIpMasked: string | null;
}

export interface DashboardTopForm {
  id: string;
  name: string;
  path: string;
  submissionsThisPeriod: number;
  successRate: number;
  lastSubmissionAt: string | null;
  status: number;
  submissionsQtyLimit: number | null;
  submissionsCount: number;
}

export interface DashboardAlertItem {
  type: string;
  severity: "info" | "warning" | "error";
  message: string;
  formId: string | null;
  formName: string | null;
  metadata: string | null;
}

export interface DashboardOnboarding {
  hasCreatedForm: boolean;
  hasReceivedSubmission: boolean;
  hasConfiguredEmail: boolean;
  hasConfiguredIntegration: boolean;
  hasConfiguredFields: boolean;
  completionPercent: number;
  showOnboarding: boolean;
}

export interface Dashboard {
  planUsage: DashboardPlanUsage;
  stats: DashboardStats;
  recentActivity: DashboardActivityItem[];
  topForms: DashboardTopForm[];
  alerts: DashboardAlertItem[];
  onboarding: DashboardOnboarding;
  generatedAt: string;
}

export interface DashboardInput {
  activityLimit?: number;
  topFormsLimit?: number;
  timezone?: string;
}

/**
 * Dashboard service that handles dashboard API calls
 */
export class DashboardService {
  /**
   * Get complete dashboard data
   */
  static async get(params?: DashboardInput): Promise<OpResult> {
    const result = await ApiService.executeQuery(DASHBOARD_GET_QUERY, {
      params: params || {},
    });
    return result;
  }
}
